// Email list screen — all Emails with create / duplicate / delete actions.
// Per spec/build-plan.md P-024 row + tech-arch.md §6.
//
// **JSON download + load dropped at P-037.31 §C.** V1's archival/restore
// mechanism (P-034 / D-096) is superseded by V2's planned cloud sync —
// the JSON pair carried no remaining V1 value once Banks ruled out
// cross-device transfer as an acceptance criterion. The Email's HTML
// download (email-editor-output.tsx) is the surviving export path.

import { useState } from 'preact/hooks'

import { sortByUpdatedDesc } from '../../lib/sort'
import { duplicateEmail } from '../../store/factories'
import type { Email } from '../../store/schemas'
import { store } from '../../store/store'
import { ConfirmModal } from '../confirm-modal'
import { EMAIL_NAME_PLACEHOLDER } from '../constants'
import { useDeleteConfirm } from '../hooks/use-delete-confirm'
import { useStore } from '../hooks/use-store'
import { NewEmailModal } from './new-email-modal'
import { RecordRow } from './record-row'

function handleDuplicate(e: Email) {
  const dup = duplicateEmail(e)
  store.setState((s) => ({ ...s, emails: [...s.emails, dup] }))
}

function handleDelete(id: string) {
  store.setState((s) => ({
    ...s,
    emails: s.emails.filter((e) => e.id !== id),
    currentEmailId: s.currentEmailId === id ? null : s.currentEmailId,
  }))
}

export function EmailListScreen() {
  const { emails, templates } = useStore()
  const sorted = sortByUpdatedDesc(emails)
  const { pending, request, onConfirm, onCancel } = useDeleteConfirm(emails, handleDelete)
  const [pickerOpen, setPickerOpen] = useState(false)

  return (
    <div class="space-y-6">
      <header class="flex flex-wrap items-baseline justify-between gap-2">
        <h2 class="m-0 text-lg font-semibold">Emails</h2>
        <div class="flex flex-wrap items-baseline gap-2">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            class="rounded bg-ypo-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-ypo-navy-deep"
          >
            New Email
          </button>
        </div>
      </header>

      {sorted.length === 0 ? (
        <p class="m-0 text-sm text-gray-600">
          No Emails yet. Click "New Email" to create your first.
        </p>
      ) : (
        <ul class="m-0 list-none space-y-2 p-0">
          {sorted.map((e) => {
            const templateLabel = e.templateName !== '' ? e.templateName : 'No template'
            const blockLabel = `${e.blocks.length} block${e.blocks.length === 1 ? '' : 's'}`
            return (
              <RecordRow
                key={e.id}
                name={e.name || EMAIL_NAME_PLACEHOLDER}
                subtitle={`${templateLabel} · ${blockLabel}`}
                editHref={`#/emails/${e.id}`}
                onDuplicate={() => handleDuplicate(e)}
                onDelete={() => request(e.id)}
              />
            )
          })}
        </ul>
      )}

      <ConfirmModal
        open={pending !== undefined}
        title="Delete this Email?"
        message={
          pending !== undefined
            ? `"${pending.name || EMAIL_NAME_PLACEHOLDER}" will be removed. This cannot be undone.`
            : ''
        }
        onConfirm={onConfirm}
        onCancel={onCancel}
      />

      <NewEmailModal
        open={pickerOpen}
        templates={templates}
        emails={emails}
        onCancel={() => setPickerOpen(false)}
      />
    </div>
  )
}
